"use client"

import { createContext, useContext, useState } from "react"
import { X, AlertTriangle, CheckCircle } from "lucide-react"

const ModalConfirmationContext = createContext()

export const useModalConfirmation = () => {
  const context = useContext(ModalConfirmationContext)
  if (!context) {
    console.error('useModalConfirmation must be used within a ModalConfirmationProvider')
  }
  return context
}

export const ModalConfirmationProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [modalData, setModalData] = useState({
    title: "Confirm Action",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "warning" // warning, success, danger
  })
  const [resolvePromise, setResolvePromise] = useState(null)

  const showConfirmation = (msg, options = {}) => {
    setModalData({
      title: options.title || "Confirm Action",
      message: msg,
      confirmText: options.confirmText || "Confirm",
      cancelText: options.cancelText || "Cancel",
      type: options.type || "warning"
    })
    setIsVisible(true)
    return new Promise((resolve) => {
      setResolvePromise(() => resolve)
    })
  }

  const handleConfirm = () => {
    setIsVisible(false)
    if (resolvePromise) resolvePromise(true)
  }

  const handleCancel = () => {
    setIsVisible(false)
    if (resolvePromise) resolvePromise(false)
  }

  const getIcon = () => {
    switch (modalData.type) {
      case "success":
        return <CheckCircle className="w-6 h-6 text-green-600" />
      case "danger":
        return <AlertTriangle className="w-6 h-6 text-red-600" />
      default:
        return <AlertTriangle className="w-6 h-6 text-amber-600" />
    }
  }

  const getButtonStyles = () => {
    switch (modalData.type) {
      case "success":
        return "bg-green-600 hover:bg-green-700 text-white"
      case "danger":
        return "bg-red-600 hover:bg-red-700 text-white"
      default:
        return "bg-amber-600 hover:bg-amber-700 text-white"
    }
  }

  return (
    <ModalConfirmationContext.Provider value={{ showConfirmation }}>
      {children}
      {isVisible && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCancel}
            />
            
            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-auto transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  {getIcon()}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {modalData.title}
                  </h3>
                </div>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {modalData.message}
                </p>
              </div>
              
              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  {modalData.cancelText}
                </button>
                <button
                  onClick={handleConfirm}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonStyles()}`}
                >
                  {modalData.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalConfirmationContext.Provider>
  )
}
